import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Reusable "send by email" dialog.
 *
 * props:
 *  - open / onClose
 *  - files: [{path, name}] selectable attachments (all pre-selected, with Select all)
 *  - defaultSubject: string
 *  - contextName: property/record name injected into the default message
 *  - getBodyHtml: optional async () => string — extra HTML appended below the
 *    message (used to email the room schedule / estimation dossier).
 */
export default function EmailShareDialog({ open, onClose, files = [], defaultSubject = "", contextName = "", getBodyHtml }) {
  const { t } = useLang();
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo("");
    setSubject(defaultSubject);
    setMessage(t("shareDefaultMessage").replace("{name}", contextName || ""));
    setSelected(files.map((f) => f.path));
    // Suggest known buyer/seller emails
    (async () => {
      try {
        const [b, s] = await Promise.all([api.get("/buyers"), api.get("/sellers")]);
        const list = [...b.data, ...s.data]
          .filter((x) => x.email)
          .map((x) => ({ email: x.email, name: x.name }));
        setContacts(list);
      } catch (err) {
        console.warn("Could not load contacts for email suggestions", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allSelected = files.length > 0 && selected.length === files.length;
  const toggleAll = () => setSelected(allSelected ? [] : files.map((f) => f.path));
  const toggle = (path) =>
    setSelected((cur) => (cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path]));

  const send = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) {
      toast.error(t("validEmailRequired"));
      return;
    }
    setSending(true);
    try {
      const msgHtml = `<div style="font-family: system-ui, sans-serif; font-size: 14px; color: #1c1917; white-space: pre-line;">${message
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>`;
      const extra = getBodyHtml ? await getBodyHtml() : "";
      await api.post("/share/email", {
        to: to.trim(),
        subject: subject.trim(),
        message_html: extra ? `${msgHtml}<hr style="border:none;border-top:1px solid #e7e5e4;margin:16px 0;" />${extra}` : msgHtml,
        attachments: files.filter((f) => selected.includes(f.path)).map((f) => ({ path: f.path, name: f.name })),
      });
      toast.success(t("emailSent"));
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const uniqueContacts = useMemo(() => {
    const seen = new Set();
    return contacts.filter((c) => (seen.has(c.email) ? false : seen.add(c.email)));
  }, [contacts]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="email-share-dialog">
        <DialogHeader><DialogTitle>{t("shareEmailTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-stone-500">{t("recipient")}</Label>
            <Input
              type="email"
              data-testid="share-email-to"
              list="share-contact-suggestions"
              placeholder="email@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <datalist id="share-contact-suggestions">
              {uniqueContacts.map((c) => (
                <option key={c.email} value={c.email}>{c.name}</option>
              ))}
            </datalist>
          </div>
          <div>
            <Label className="text-xs text-stone-500">{t("subject")}</Label>
            <Input data-testid="share-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-stone-500">{t("message")}</Label>
            <textarea
              data-testid="share-email-message"
              rows={5}
              className="w-full text-sm border border-stone-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          {files.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-stone-500">{t("selectFilesToSend")}</Label>
                <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer">
                  <Checkbox data-testid="share-select-all" checked={allSelected} onCheckedChange={toggleAll} />
                  {t("selectAll")}
                </label>
              </div>
              <ul className="max-h-40 overflow-y-auto space-y-1 border border-stone-200 rounded-lg p-2">
                {files.map((f, i) => (
                  <li key={f.path}>
                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                      <Checkbox
                        data-testid={`share-file-check-${i}`}
                        checked={selected.includes(f.path)}
                        onCheckedChange={() => toggle(f.path)}
                      />
                      <span className="truncate">{f.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="border-stone-300" data-testid="share-email-cancel">{t("cancel")}</Button>
            <Button data-testid="share-email-send" onClick={send} disabled={sending} className="bg-stone-900 hover:bg-stone-800 text-white">
              <Send size={14} className="mr-1.5" /> {sending ? "…" : t("sendEmail")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
